import {
  PLAN_LIMITS,
  planTemplate,
  validatePlan,
  type PlanFile,
  type PlanStep,
  type PlanStepType,
  type ProjectType,
  type TestCreateRequest,
  type ValidationIssue,
} from '@verifyloop/core';

export const PLAN_STEP_TYPE_OPTIONS: readonly { value: PlanStepType; label: string }[] = [
  { value: 'action', label: 'action: interact with the app (click, type, select)' },
  { value: 'assertion', label: 'assertion: something must be true, or the run fails' },
  { value: 'navigation', label: 'navigation: go to a URL or route' },
  { value: 'extract', label: 'extract: capture a value for later steps' },
  { value: 'wait', label: 'wait: pause, or wait for a condition' },
  { value: 'setup', label: 'setup: precondition for the steps after it' },
  { value: 'teardown', label: 'teardown: cleanup, runs even when earlier steps fail' },
  { value: 'dependency', label: 'dependency: an API call the UI depends on' },
];

export const PROJECT_TYPE_OPTIONS: readonly { value: ProjectType; label: string }[] = [
  { value: 'frontend', label: 'frontend: driven in a real browser' },
  { value: 'backend', label: 'backend: driven over HTTP, no browser' },
];

/** A two-step starter that matches the real schema and passes validation. */
export function starterPlan(type: ProjectType = 'frontend', projectId?: string): PlanFile {
  const plan = planTemplate(projectId);
  plan.type = type;
  if (type === 'backend') {
    plan.name = 'Health endpoint responds';
    plan.planSteps = [
      { type: 'navigation', description: 'GET /api/health', target: '/api/health' },
      { type: 'assertion', description: 'Verify the body reports status "ok"' },
    ];
  }
  return plan;
}

export function emptyStep(type: PlanStepType = 'action'): PlanStep {
  return { type, description: '' };
}

/** Deep copy that keeps only the fields the plan schema knows about. */
export function normalizePlan(input: unknown): PlanFile {
  const source = (input ?? {}) as Partial<PlanFile>;
  const steps: PlanStep[] = Array.isArray(source.planSteps)
    ? source.planSteps.map((step) => {
        const candidate = (step ?? {}) as PlanStep;
        const normalized: PlanStep = {
          type: candidate.type,
          description: candidate.description ?? '',
        };
        if (candidate.target !== undefined && candidate.target !== '') normalized.target = candidate.target;
        if (candidate.optional) normalized.optional = true;
        if (candidate.durationMs !== undefined) normalized.durationMs = candidate.durationMs;
        if (Array.isArray(candidate.dependsOn) && candidate.dependsOn.length > 0) {
          normalized.dependsOn = candidate.dependsOn;
        }
        if (candidate.locator) normalized.locator = candidate.locator;
        if (candidate.assert) normalized.assert = candidate.assert;
        if (candidate.action) normalized.action = candidate.action;
        return normalized;
      })
    : [];

  const plan: PlanFile = {
    type: source.type === 'backend' ? 'backend' : 'frontend',
    name: source.name ?? '',
    planSteps: steps,
  };
  if (source.$schema) plan.$schema = source.$schema;
  if (source.version !== undefined) plan.version = source.version;
  if (source.projectId) plan.projectId = source.projectId;
  if (source.environmentId) plan.environmentId = source.environmentId;
  if (source.description) plan.description = source.description;
  if (Array.isArray(source.tags) && source.tags.length > 0) plan.tags = source.tags;
  if (typeof source.stepTimeoutMs === 'number') plan.stepTimeoutMs = source.stepTimeoutMs;
  return plan;
}

export function serializePlan(plan: PlanFile): string {
  return `${JSON.stringify(normalizePlan(plan), null, 2)}\n`;
}

export function parsePlanJson(text: string): { plan: PlanFile } | { error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'that is not valid JSON' };
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return { error: 'a plan must be a JSON object' };
  }
  return { plan: normalizePlan(parsed) };
}

/** The body `POST /v1/tests` wants: plan fields inline, plus the project id. */
export function toTestCreateBody(plan: PlanFile, projectId?: string): TestCreateRequest {
  const normalized = normalizePlan(plan);
  return {
    ...(projectId ? { projectId } : {}),
    ...(normalized.environmentId ? { environmentId: normalized.environmentId } : {}),
    type: normalized.type,
    name: normalized.name,
    ...(normalized.description ? { description: normalized.description } : {}),
    ...(normalized.tags && normalized.tags.length > 0 ? { tags: normalized.tags } : {}),
    ...(normalized.stepTimeoutMs !== undefined ? { stepTimeoutMs: normalized.stepTimeoutMs } : {}),
    planSteps: normalized.planSteps,
  };
}

export function localPlanIssues(plan: PlanFile): ValidationIssue[] {
  return validatePlan(normalizePlan(plan));
}

export function downloadPlanFile(plan: PlanFile, filename = 'verifyloop.plan.json'): void {
  const blob = new Blob([serializePlan(plan)], { type: 'application/json' });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

export { PLAN_LIMITS };